import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Button } from "@shared/components/ui/button";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";
import { Badge } from "@shared/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Progress } from "@shared/components/ui/progress";
import {
  Package,
  Truck,
  CheckCircle,
  AlertCircle,
  Clock,
  MapPin,
  User,
  Phone,
  Calendar,
  Warehouse,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import * as packagesService from "../services/packagesService";

// Enhanced pickup status options aligned with package status system
const ENHANCED_PICKUP_STATUS_OPTIONS = [
  {
    value: "order_received",
    label: "Pickup Request Received",
    description: "New pickup request submitted",
    color: "bg-blue-500",
    icon: Clock,
    order: 0,
    nextSteps: ["order_confirmed", "return_collection"],
  },
  {
    value: "order_confirmed",
    label: "Pickup Confirmed",
    description: "Pickup request approved and confirmed",
    color: "bg-green-500",
    icon: CheckCircle,
    order: 1,
    nextSteps: ["to_pick_up"],
  },
  {
    value: "to_pick_up",
    label: "Ready for Pickup",
    description: "Assigned to livreur, ready for collection",
    color: "bg-yellow-500",
    icon: MapPin,
    order: 2,
    nextSteps: ["picked_up"],
  },
  {
    value: "picked_up",
    label: "Picked Up",
    description: "Packages collected from pickup location",
    color: "bg-blue-600",
    icon: Package,
    order: 3,
    nextSteps: ["in_transit"],
  },
  {
    value: "in_transit",
    label: "In Transit",
    description: "On the way to warehouse",
    color: "bg-purple-500",
    icon: Truck,
    order: 4,
    nextSteps: ["arrived_at_warehouse", "return_collection"],
  },
  {
    value: "arrived_at_warehouse",
    label: "Arrived at Warehouse",
    description: "Packages arrived at warehouse",
    color: "bg-indigo-500",
    icon: Warehouse,
    order: 5,
    nextSteps: ["signed"],
  },
  {
    value: "signed",
    label: "Delivered to Warehouse",
    description: "Successfully delivered and signed for",
    color: "bg-green-600",
    icon: CheckCircle,
    order: 6,
    nextSteps: [],
  },
  {
    value: "return_collection",
    label: "Failed/Returned",
    description: "Failed to complete pickup/delivery",
    color: "bg-red-500",
    icon: AlertCircle,
    order: 6,
    nextSteps: ["to_pick_up"], // Can retry from pickup assignment
  },
];

// Quick action templates for pickup scenarios
const PICKUP_QUICK_ACTIONS = {
  confirm_pickup: {
    status: "order_confirmed",
    note: "Pickup request confirmed and approved for collection",
  },
  assign_for_pickup: {
    status: "to_pick_up",
    note: "Assigned to livreur for pickup collection",
  },
  successful_collection: {
    status: "picked_up",
    note: "All packages collected successfully from pickup location",
  },
  en_route_to_warehouse: {
    status: "in_transit",
    note: "Packages loaded and heading to warehouse",
  },
  arrived_warehouse: {
    status: "arrived_at_warehouse",
    note: "Packages have arrived at warehouse facility",
  },
  delivered_to_warehouse: {
    status: "signed",
    note: "All packages delivered safely to warehouse and signed for",
  },
  pickup_failed: {
    status: "return_collection",
    note: "Unable to complete pickup - location closed or packages not ready",
  },
};

export function EnhancedPickupStatusManager({ pickupData, trigger, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [note, setNote] = useState("");
  const [isQuickAction, setIsQuickAction] = useState(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Calculate pickup progress
  const currentStatusOption = ENHANCED_PICKUP_STATUS_OPTIONS.find(
    (option) => option.value === pickupData.status
  );
  const selectedStatusOption = ENHANCED_PICKUP_STATUS_OPTIONS.find(
    (option) => option.value === selectedStatus
  );

  const getPickupProgress = () => {
    const statusOrder = currentStatusOption?.order || 0;
    return Math.min((statusOrder / 6) * 100, 100);
  };

  // Get available next steps based on current status
  const getAvailableStatuses = () => {
    if (!currentStatusOption?.nextSteps?.length) {
      return ENHANCED_PICKUP_STATUS_OPTIONS.filter(
        (option) => option.order > (currentStatusOption?.order || 0)
      );
    }
    return ENHANCED_PICKUP_STATUS_OPTIONS.filter((option) =>
      currentStatusOption.nextSteps.includes(option.value)
    );
  };

  // Update pickup status mutation
  const updatePickupStatusMutation = useMutation({
    mutationFn: ({ status, note }) =>
      packagesService.updatePickupStatusByLivreur(pickupData._id, {
        status,
        note,
      }),
    onSuccess: (data) => {
      // Invalidate relevant query keys
      const queriesToInvalidate = [
        ["pickups"],
        ["pickup", pickupData._id],
        ["pickup-stats"],
        ["pickup-analytics"],
        ["recent-pickups"],
        ["livreur-pickups"],
        ["livreur-pickups-widget"],
        ["livreur-dashboard"],
        ["livreur-packages"],
        ["dashboard"],
        ["dashboard-stats"],
        ["packages"],
      ];

      queriesToInvalidate.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });

      toast.success(t("Pickup status updated successfully"), {
        description: `Status changed to ${t(selectedStatusOption?.label)}`,
      });

      setOpen(false);
      setSelectedStatus("");
      setNote("");
      setIsQuickAction(false);

      // Call onUpdate callback if provided
      onUpdate?.(data);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.message || t("Failed to update pickup status");
      toast.error(t("Update Failed"), {
        description: errorMessage,
      });
    },
  });

  const handleQuickAction = (actionKey) => {
    const action = PICKUP_QUICK_ACTIONS[actionKey];
    setSelectedStatus(action.status);
    setNote(action.note);
    setIsQuickAction(true);
  };

  const handleUpdateStatus = () => {
    if (!selectedStatus) {
      toast.error(t("Please select a status"));
      return;
    }

    if (selectedStatus === pickupData.status) {
      toast.error(t("Please select a different status"));
      return;
    }

    updatePickupStatusMutation.mutate({
      status: selectedStatus,
      note: note.trim(),
    });
  };

  const formatDateTime = (date) => {
    return format(new Date(date), "MMM dd, yyyy HH:mm");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            {t("Update Pickup Status")}
          </DialogTitle>
          <DialogDescription>
            {t("Manage the pickup status")} #{pickupData._id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Pickup Overview Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>{t("Pickup Information")}</span>
                <Badge variant="outline" className="flex items-center gap-1">
                  {currentStatusOption?.icon && (
                    <currentStatusOption.icon className="h-3 w-3" />
                  )}
                  {t(currentStatusOption?.label || pickupData.status)}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pickup Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t("Pickup Progress")}</span>
                  <span>{Math.round(getPickupProgress())}%</span>
                </div>
                <Progress value={getPickupProgress()} className="h-2" />
              </div>

              {/* Pickup Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {pickupData.name || "No name provided"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{pickupData.phone || "No phone provided"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{pickupData.address || "No address provided"}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {t("Created")}: {formatDateTime(pickupData.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {t("Packages")}: {pickupData.packages?.length || 0}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">{t("Type")}:</span>{" "}
                    {pickupData.type || "package"}
                  </div>
                </div>
              </div>

              {/* Existing Note */}
              {pickupData.note && (
                <div className="p-3 bg-muted rounded-lg">
                  <div className="text-sm font-medium mb-1">
                    {t("Previous Note")}:
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {pickupData.note}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("Quick Actions")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("confirm_pickup")}
                  className="justify-start"
                >
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                  {t("Confirm Pickup")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("assign_for_pickup")}
                  className="justify-start"
                >
                  <MapPin className="h-4 w-4 mr-2 text-yellow-500" />
                  {t("Assign for Pickup")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("successful_collection")}
                  className="justify-start"
                >
                  <Package className="h-4 w-4 mr-2 text-blue-500" />
                  {t("Successful Collection")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("en_route_to_warehouse")}
                  className="justify-start"
                >
                  <Truck className="h-4 w-4 mr-2 text-purple-500" />
                  {t("En Route to Warehouse")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("arrived_warehouse")}
                  className="justify-start"
                >
                  <Warehouse className="h-4 w-4 mr-2 text-indigo-500" />
                  {t("Arrived at Warehouse")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("delivered_to_warehouse")}
                  className="justify-start"
                >
                  <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                  {t("Delivered to Warehouse")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction("pickup_failed")}
                  className="justify-start"
                >
                  <AlertCircle className="h-4 w-4 mr-2 text-red-500" />
                  {t("Pickup Failed")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Status Selection */}
          <div className="space-y-3">
            <Label htmlFor="status" className="text-base font-medium">
              {t("Select New Status")}
            </Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t("Choose pickup status")} />
              </SelectTrigger>
              <SelectContent>
                {getAvailableStatuses().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${option.color}`} />
                      <option.icon className="h-4 w-4" />
                      <div className="flex flex-col">
                        <span className="font-medium">{t(option.label)}</span>
                        <span className="text-xs text-muted-foreground">
                          {t(option.description)}
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Preview */}
          {selectedStatusOption && (
            <Card className="border-2 border-dashed">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div
                    className={`w-4 h-4 rounded-full ${selectedStatusOption.color} mt-0.5`}
                  />
                  <div className="space-y-1">
                    <div className="font-medium">
                      {t(selectedStatusOption.label)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t(selectedStatusOption.description)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Note */}
          <div className="space-y-3">
            <Label htmlFor="note" className="text-base font-medium">
              {t("Update Notes")}{" "}
              {isQuickAction && (
                <span className="text-sm text-muted-foreground">
                  ({t("pre-filled")})
                </span>
              )}
            </Label>
            <Textarea
              id="note"
              placeholder={t(
                "Add details about the pickup status update, any issues encountered, or additional information..."
              )}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <div className="text-xs text-muted-foreground">
              {t(
                "Detailed notes help track pickup progress and resolve any issues"
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
              setSelectedStatus("");
              setNote("");
              setIsQuickAction(false);
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleUpdateStatus}
            disabled={
              updatePickupStatusMutation.isPending ||
              !selectedStatus ||
              selectedStatus === pickupData.status
            }
            className="min-w-[120px]"
          >
            {updatePickupStatusMutation.isPending ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t("Updating...")}
              </div>
            ) : (
              t("Update Status")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EnhancedPickupStatusManager;
